function roundMoney(value) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function monthKeyFromDate(value) {
  const match = /^(\d{4}-\d{2})/.exec(String(value || ''));
  return match ? match[1] : '';
}

function shiftMonth(month, delta) {
  const match = /^(\d{4})-(\d{2})$/.exec(month || '');
  if (!match) return '';
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1 + delta, 1));
  const year = date.getUTCFullYear();
  const shifted = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${shifted}`;
}

export function commitmentMonths(effectiveDate, commitmentLength) {
  const start = monthKeyFromDate(effectiveDate);
  const count = Number(commitmentLength);
  if (!start || !Number.isInteger(count) || count <= 0) return [];
  return Array.from({ length: count }, (_, index) => shiftMonth(start, index));
}

export function totalFacilityFee(customer) {
  return roundMoney((customer?.months || []).reduce(
    (sum, month) => sum + (Number(month?.facilityFee) || 0),
    0
  ));
}

function spreadFacilityFee(total, months) {
  const placed = new Map();
  if (!total || !months.length) return placed;
  const monthly = roundMoney(total / months.length);
  let allocated = 0;
  months.forEach((month, index) => {
    const amount = index === months.length - 1 ? roundMoney(total - allocated) : monthly;
    allocated = roundMoney(allocated + amount);
    if (amount) placed.set(month, amount);
  });
  return placed;
}

// chargedFacilityFee is the facility fee in the months the cashflow charges it.
function chargedFacilityFee(customer) {
  const placed = new Map();
  (customer?.months || []).forEach((row) => {
    const amount = Number(row?.facilityFee) || 0;
    if (!amount || !row?.month) return;
    placed.set(row.month, roundMoney((placed.get(row.month) || 0) + amount));
  });
  return placed;
}

// facilityFeeByMonth spreads a customer's facility fees evenly over the commitment
// of its deal. A deal without a commitment (no effective date or length) keeps
// the fees in the months they are charged, so the tab still adds up to the
// cashflow, S&U and company cash.
function facilityFeeByMonth(customer, deal) {
  const months = commitmentMonths(deal?.effectiveDate, deal?.commitmentLength);
  if (!months.length) return chargedFacilityFee(customer);
  return spreadFacilityFee(totalFacilityFee(customer), months);
}

export function facilityFeeMonthKeys(customers, dealTerms, startMonth, endMonth) {
  const keys = new Set();
  (customers || []).forEach((customer) => {
    const deal = dealTerms?.[customer.customerId];
    [...facilityFeeByMonth(customer, deal).keys(), ...commitmentMonths(deal?.effectiveDate, deal?.commitmentLength)].forEach((month) => {
      if (startMonth && month < startMonth) return;
      if (endMonth && month > endMonth) return;
      keys.add(month);
    });
  });
  return [...keys].sort();
}

export function buildFacilityFeeRows(customers, dealTerms, monthKeys) {
  const rows = (customers || []).map((customer) => {
    const byMonth = facilityFeeByMonth(customer, dealTerms?.[customer.customerId]);
    return {
      id: customer.customerId,
      label: customer.companyName || customer.customerId,
      values: (monthKeys || []).map((month) => byMonth.get(month) || null),
    };
  });
  const total = (monthKeys || []).map((_, index) => {
    const sum = rows.reduce((acc, row) => acc + (row.values[index] || 0), 0);
    return sum ? roundMoney(sum) : null;
  });
  return [...rows, { id: 'total', label: 'Total', values: total }];
}
